import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";

import { IGerenteSummary } from "@/@types/regional-substitution";

import {
  RegionalSubstitutionFormData,
  regionalSubstitutionFormSchema,
} from "../validations/regional-substitution.validation";
import { RegionalCombobox } from "./regional-combobox";
import { UserCombobox } from "./user-combobox";

interface SubstitutionFormProps {
  defaultValues: Partial<RegionalSubstitutionFormData>;
  peopleDisabled: boolean;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (data: RegionalSubstitutionFormData) => void;
  onCancel: () => void;
  gerenteFallback?: IGerenteSummary;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <span className="text-sm text-red-600">{message}</span> : null;

export function SubstitutionForm({
  defaultValues,
  peopleDisabled,
  submitting,
  submitLabel,
  onSubmit,
  onCancel,
  gerenteFallback,
}: SubstitutionFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegionalSubstitutionFormData>({
    resolver: zodResolver(regionalSubstitutionFormSchema),
    defaultValues,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>Dados da Substituição</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Regional (titular)</Label>
            <Controller
              control={control}
              name="regional_id"
              render={({ field }) => (
                <RegionalCombobox
                  value={field.value}
                  onSelect={field.onChange}
                  disabled={peopleDisabled}
                />
              )}
            />
            <FieldError message={errors.regional_id?.message} />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Gerente (substituto)</Label>
            <Controller
              control={control}
              name="user_id"
              render={({ field }) => (
                <UserCombobox
                  value={field.value}
                  onSelect={field.onChange}
                  disabled={peopleDisabled}
                  fallback={gerenteFallback}
                />
              )}
            />
            <FieldError message={errors.user_id?.message} />
          </div>

          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-2">
              <Label htmlFor="initial_term">Início da vigência</Label>
              <Input
                id="initial_term"
                type="date"
                {...register("initial_term")}
              />
              <FieldError message={errors.initial_term?.message} />
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <Label htmlFor="final_term">Fim da vigência</Label>
              <Input
                id="final_term"
                type="date"
                {...register("final_term")}
              />
              <FieldError message={errors.final_term?.message} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Salvando..." : submitLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
